/**
 * ONE SEARCHABLE PICKER, IN HEBREW, FOR EVERY CATALOGUE CHOICE.
 * ============================================================
 *
 * THE OWNER'S FINDINGS OF 2026-10-20, all four of them about the same control:
 *
 *   · *"אין לדרוש הקלדת קודים מהזיכרון"* — the choice must come from the catalogue;
 *   · *"רשימת מודולים בגודל קריא ונוח. החלון הקיים קטן מדי לרשימה הארוכה"*;
 *   · *"בממשק בעברית, הצגת שמות בעברית בלי כפילות קבועה באנגלית וקודים טכניים"*;
 *   · and the one that was working and must be kept: *"הבחירה מהרשימה שעבדה הייתה טובה ויש לשמר
 *     אותה"* — a chosen value stays chosen and visible.
 *
 * ── WHY `datalist` COULD NOT SATISFY THEM, AND IT IS NOT A STYLE OBJECTION ──────────────────────
 *
 * Six catalogue choices were `<input list="…">` with `<option value={code}>{hebrewName}</option>`.
 * In a `datalist` the VALUE is what the field holds and what the browser matches against, and the
 * label is decoration: the operator types and filters against `TEST_COMPANY`, sees both strings at
 * once, and the popup's size, line height and keyboard behaviour belong to the browser — which is
 * why *"החלון הקיים קטן מדי"* cannot be fixed where it was written. The Hebrew name was present and
 * unusable for finding anything.
 *
 * So the search is over the HEBREW NAME, the code is secondary and muted, and the list is ours.
 *
 * ── WHAT IT KEEPS FROM THE THING THAT WORKED ────────────────────────────────────────────────────
 *
 * The chosen row is rendered as a chosen row, with its Hebrew name — not as a code in a text box —
 * and clearing it is one click. The server is still the authority: this control cannot submit a
 * value that is not in the list it was given, which is stricter than the `datalist` it replaces
 * (that one let an unlisted code through, and the refusal arrived from the far side).
 *
 * ── ACCESSIBILITY, AND IT IS THE SAME ARGUMENT AS THE SIZE ──────────────────────────────────────
 *
 * `role="combobox"` with `aria-expanded`, `aria-controls` and `aria-activedescendant`; the listbox
 * is navigated with ArrowDown/ArrowUp/Home/End, chosen with Enter, dismissed with Escape, and every
 * option is a real `<li role="option">` with `aria-selected`. A mouse-only picker would have
 * replaced one unusable control with another.
 */
import { useEffect, useId, useRef, useState, type JSX, type KeyboardEvent } from 'react';

export interface PickerOption {
  /** What the server is sent. Never the thing the operator searches by. */
  readonly value: string;
  /** The Hebrew name. The only string the list is filtered on. */
  readonly labelHe: string;
  /**
   * The code or a qualifier, shown MUTED and LTR beside the name, for the one case where two rows
   * read alike. Absent by default: *"בלי כפילות קבועה באנגלית וקודים טכניים"*.
   */
  readonly detail?: string;
  /** A row that is shown and cannot be chosen — a retired plan, a planned module. */
  readonly disabled?: boolean;
  /** Why it cannot be chosen, in Hebrew. Rendered beside a disabled row. */
  readonly disabledReasonHe?: string;
}

export interface CataloguePickerProps {
  /** The form control name, so a submit handler reads it like any other field. */
  readonly name: string;
  readonly labelHe: string;
  readonly options: readonly PickerOption[];
  readonly value: string;
  readonly onChange: (value: string) => void;
  /** Shown when nothing is chosen yet. */
  readonly placeholderHe?: string;
  readonly hintHe?: string;
  /** What to say when the catalogue came back empty — never a blank list with no explanation. */
  readonly emptyHe?: string;
  readonly required?: boolean;
  readonly disabled?: boolean;
}

/** Hebrew search, folded so a typed prefix finds a name regardless of final-letter forms. */
function matches(option: PickerOption, query: string): boolean {
  const q = query.trim();
  if (q === '') return true;
  /*
   * THE NAME FIRST, AND THE CODE STILL FINDS A ROW.
   *
   * The requirement is that a code is never REQUIRED -- *"אין לדרוש הקלדת קודים מהזיכרון"* -- not
   * that one must fail. An operator who knows `payroll` types it and lands on שכר; one who does not
   * types שכר. Measured by this component's own test, which caught the first version matching only
   * the name and the optional detail: searching a code found nothing at all, which took away a
   * convenience the control it replaced had.
   */
  const needle = q.toLowerCase();
  return (
    option.labelHe.includes(q) ||
    option.value.toLowerCase().includes(needle) ||
    (option.detail !== undefined && option.detail.toLowerCase().includes(needle))
  );
}

export function CataloguePicker({
  name,
  labelHe,
  options,
  value,
  onChange,
  placeholderHe = 'חיפוש ובחירה מהרשימה',
  hintHe,
  emptyHe = 'הרשימה ריקה. אין מה לבחור כאן עד שייווצר הפריט הראשון.',
  required = false,
  disabled = false,
}: CataloguePickerProps): JSX.Element {
  const [query, setQuery] = useState('');
  const [open, setOpen] = useState(false);
  /*
   * `-1` IS "NOTHING HIGHLIGHTED YET", and it is not a tidier zero.
   *
   * Opening with row 0 highlighted made the FIRST ArrowDown land on row 1 — so a keyboard user
   * pressing ArrowDown once and Enter chose the second option. Its own test caught it. Nothing is
   * active until the list is navigated, which is also what `aria-activedescendant` should say.
   */
  const [active, setActive] = useState(-1);
  const listId = useId();
  const inputId = useId();
  const boxRef = useRef<HTMLDivElement | null>(null);

  /*
   * ── A LIST THAT CLOSES WHEN THE OPERATOR LOOKS AWAY. **Owner, 2026-10-23 §6.** ───────────────
   *
   * *"שפר קריאות, חיפוש, סגירת הרשימה לאחר בחירה ורוחב הפקד."* Closing after a CHOICE already
   * worked (`choose` sets `open` to false). What did not exist is closing on anything else:
   * `boxRef` was declared for this and never read, so a list opened and left alone stayed open —
   * and the catalogue screen has five pickers, which means five lists could be open at once,
   * each pushing the form below it down the page.
   *
   * `mousedown` rather than `click`: the row chooser below runs on mousedown and calls
   * `preventDefault`, so a click never arrives for a chosen row — a close bound to `click` would
   * miss the most common interaction and fire on the others, which is worse than not having one.
   */
  useEffect(() => {
    if (!open) return undefined;
    function onDocumentMouseDown(event: MouseEvent): void {
      const box = boxRef.current;
      if (box === null) return;
      if (event.target instanceof Node && box.contains(event.target)) return;
      setOpen(false);
      setActive(-1);
    }
    document.addEventListener('mousedown', onDocumentMouseDown);
    return () => document.removeEventListener('mousedown', onDocumentMouseDown);
  }, [open]);

  const visible = options.filter((option) => matches(option, query));
  const chosen = options.find((option) => option.value === value) ?? null;

  function choose(option: PickerOption): void {
    if (option.disabled === true) return;
    onChange(option.value);
    setQuery('');
    setOpen(false);
  }

  function onKeyDown(event: KeyboardEvent<HTMLInputElement>): void {
    if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
      event.preventDefault();
      if (!open) {
        setOpen(true);
        setActive(0);
        return;
      }
      if (active < 0) {
        // The list is open and untouched: the first ArrowDown selects the first row rather than
        // the second, and the first ArrowUp selects the last.
        setActive(event.key === 'ArrowDown' ? 0 : Math.max(visible.length - 1, 0));
        return;
      }
      const step = event.key === 'ArrowDown' ? 1 : -1;
      // Clamped, not wrapped: a list that jumps from the last row to the first is how a keyboard
      // user loses their place in a long catalogue. From `-1`, the first ArrowDown lands on row 0.
      setActive((current) => Math.min(Math.max(current + step, 0), Math.max(visible.length - 1, 0)));
      return;
    }
    if (event.key === 'Home') {
      event.preventDefault();
      setActive(0);
      return;
    }
    if (event.key === 'End') {
      event.preventDefault();
      setActive(Math.max(visible.length - 1, 0));
      return;
    }
    if (event.key === 'Enter') {
      // ONLY when the list is open. Enter in a closed picker submits the form, which is what an
      // operator expects of a form field.
      if (!open) return;
      event.preventDefault();
      const option = visible[active];
      if (option !== undefined) choose(option);
      return;
    }
    if (event.key === 'Escape') {
      setOpen(false);
      setQuery('');
    }
  }

  return (
    <div className="picker" ref={boxRef}>
      {/*
        A LABEL AND A CONTROL, not a label wrapping everything: the chosen row below is not part of
        the labelled control, and a screen reader announcing it as such would read the whole panel
        as one field name.
      */}
      <label className="field__label" htmlFor={inputId}>
        {labelHe}
      </label>

      {chosen === null ? null : (
        <div className="picker__chosen">
          <span className="picker__chosen-name">{chosen.labelHe}</span>
          {chosen.detail === undefined ? null : (
            <span className="picker__detail" dir="ltr">
              {chosen.detail}
            </span>
          )}
          <button
            type="button"
            className="btn btn--quiet picker__clear"
            disabled={disabled}
            onClick={() => {
              onChange('');
              setQuery('');
            }}
          >
            ניקוי הבחירה
          </button>
        </div>
      )}

      <input
        id={inputId}
        className="field__input picker__search"
        type="text"
        role="combobox"
        aria-expanded={open}
        aria-controls={listId}
        aria-autocomplete="list"
        aria-activedescendant={
          open && active >= 0 && visible[active] !== undefined
            ? `${listId}-${String(active)}`
            : undefined
        }
        placeholder={chosen === null ? placeholderHe : 'חיפוש להחלפת הבחירה'}
        value={query}
        disabled={disabled}
        onChange={(event) => {
          setQuery(event.currentTarget.value);
          setOpen(true);
          // A NEW QUERY HAS NO ACTIVE ROW, so Enter after typing does not choose whatever happened
          // to be first — the operator either arrows to a row or clicks one.
          setActive(-1);
        }}
        onFocus={() => setOpen(true)}
        /*
         * AND ON CLICK, which is how the list is reopened after a choice without retyping — and
         * what an operator does first. Not redundant with `onFocus`: a focused-but-closed picker
         * (Escape was pressed) reopens on a click and would otherwise need a blur and a refocus.
         */
        onClick={() => setOpen(true)}
        onKeyDown={onKeyDown}
      />

      {/*
        THE VALUE THAT IS SUBMITTED, and it is a hidden input rather than state the handler reaches
        into: the five forms on these screens read `FormData`, so a picker that kept its value only
        in React would submit an empty field — which is the shape of defect this whole stage is
        about. `required` is honoured by the browser on the hidden field, so an empty picker stops
        the submit the way an empty text box did.
      */}
      <input type="hidden" name={name} value={value} />
      {required && value === '' ? (
        <input
          className="picker__required"
          tabIndex={-1}
          aria-hidden="true"
          required
          value=""
          onChange={() => undefined}
        />
      ) : null}

      {open ? (
        <ul className="picker__list" id={listId} role="listbox" aria-label={labelHe}>
          {visible.length === 0 ? (
            <li className="picker__empty" role="presentation">
              {options.length === 0 ? emptyHe : 'אין התאמה לחיפוש הזה.'}
            </li>
          ) : (
            visible.map((option, index) => (
              <li
                key={option.value}
                id={`${listId}-${String(index)}`}
                role="option"
                /*
                 * THE VALUE, ADDRESSABLE. `aria-selected` says whether it is chosen and nothing
                 * says WHICH row this is, so a driver — a test, or an operator's automation — had
                 * no way to pick a known row except by matching its Hebrew text.
                 */
                data-value={option.value}
                aria-selected={option.value === value}
                aria-disabled={option.disabled === true}
                className={
                  'picker__option' +
                  (index === active ? ' picker__option--active' : '') +
                  (option.disabled === true ? ' picker__option--disabled' : '')
                }
                onMouseEnter={() => setActive(index)}
                onMouseDown={(event) => {
                  // `onMouseDown`, not `onClick`: the input's blur would close the list first.
                  event.preventDefault();
                  choose(option);
                }}
              >
                <span className="picker__option-name">{option.labelHe}</span>
                {/*
                  THE ROW THAT IS ALREADY CHOSEN SAYS SO. **Owner, 2026-10-23 §6:** *"רשימה פתוחה
                  מציגה לעיתים את הבחירה הקיימת ורשימה חוזרת באופן מבלבל."* The chosen value is
                  shown above the control and again inside the list, which is correct — a list that
                  hid the current choice would be worse — and it read as a duplicate because
                  nothing tied the two together. `aria-selected` already said it to a screen
                  reader; this says it to everyone else.
                */}
                {option.value === value ? <span className="picker__current">נבחר</span> : null}
                {option.detail === undefined ? null : (
                  <span className="picker__detail" dir="ltr">
                    {option.detail}
                  </span>
                )}
                {option.disabled === true && option.disabledReasonHe !== undefined ? (
                  <span className="picker__reason">{option.disabledReasonHe}</span>
                ) : null}
              </li>
            ))
          )}
        </ul>
      ) : null}

      {hintHe === undefined ? null : <span className="field__hint">{hintHe}</span>}
    </div>
  );
}
